// Supplier portal home (Lane 1331; new look Lane 1394): choose a buyer, then
// that buyer's events and notices. Only one buyer's data is on screen at a time: switching buyers
// clears the lists first, and an answer for a buyer no longer chosen is dropped.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PortalShell, { usePortalSession } from './PortalShell';
import { portalApi, getChosenBuyer, setChosenBuyer } from './portalApi';
import { EventStatus, OWN_STATUS_TEXT, formatDueAt, timeLeftText, zoneName } from './portalView';

const OWN_TONE = { submitted: 'srcp-pill--ok', viewed: 'srcp-pill--info', declined: '', not_started: 'srcp-pill--line' };

function Tile({ k, v }) {
  return (
    <div className="srcp-tile">
      <span className="srcp-tile-k">{k}</span>
      <span className="srcp-tile-v">{Number(v).toLocaleString()}</span>
    </div>
  );
}

function pickInitialBuyer(buyers) {
  if (buyers.length === 1) return buyers[0].membership_id;
  const saved = getChosenBuyer();
  return buyers.some((b) => b.membership_id === saved) ? saved : null;
}

function sortNotices(items) {
  return [...items].sort((a, b) => {
    if (a.read !== b.read) return a.read ? 1 : -1;
    return String(b.created_at || '').localeCompare(String(a.created_at || ''));
  });
}

function BuyerView({ membershipId }) {
  const navigate = useNavigate();
  const [events, setEvents] = useState(null);
  const [notices, setNotices] = useState(null);
  const [error, setError] = useState('');
  const current = useRef(membershipId);
  current.current = membershipId;

  const loadNotices = useCallback(() => portalApi.notices(membershipId)
    .then((r) => { if (current.current === membershipId) setNotices(sortNotices(r.items || [])); }),
  [membershipId]);

  useEffect(() => {
    setEvents(null);
    setNotices(null);
    setError('');
    Promise.all([portalApi.events(membershipId), portalApi.notices(membershipId)])
      .then(([ev, no]) => {
        if (current.current !== membershipId) return;
        setEvents(ev.items || []);
        setNotices(sortNotices(no.items || []));
      })
      .catch((e) => { if (current.current === membershipId && e.status !== 401) setError(e.message); });
  }, [membershipId]);

  const markRead = async (noticeId) => {
    try {
      await portalApi.readNotice(noticeId);
      await loadNotices();
    } catch (e) {
      if (e.status !== 401) setError(e.message);
    }
  };

  if (error) return <div className="srcp-error" role="alert">{error}</div>;
  if (events === null || notices === null) return <div className="srcp-loading">Loading events…</div>;
  const now = Date.now();
  const open = events.filter((e) => e.status === 'open');
  const dueSoon = open.filter((e) => {
    const t = e.due_at ? new Date(e.due_at).getTime() : NaN;
    return Number.isFinite(t) && t > now && t - now <= 7 * 86400000;
  });
  const unread = notices.filter((n) => !n.read).length;
  const openEvent = (id) => navigate(`/sourcing-portal/events/${encodeURIComponent(id)}`);
  return (
    <>
      <section className="srcp-card srcp-tiles" aria-label="Summary">
        <Tile k="Open events" v={open.length} />
        <Tile k="Due this week" v={dueSoon.length} />
        <Tile k="Submitted" v={events.filter((e) => e.own_status === 'submitted').length} />
        <Tile k="Unread messages" v={unread} />
      </section>

      <section className="srcp-card">
        <div className="srcp-card-h">
          <span className="srcp-card-t">Events you are invited to</span>
          <span className="srcp-mut">Open an event to quote.</span>
        </div>
        {events.length === 0 && <div className="srcp-card-b srcp-mut">No events from this buyer yet.</div>}
        {events.length > 0 && (
          <div className="srcp-scroll">
            <table className="srcp-grid">
              <thead>
                <tr>
                  <th>Event</th><th className="srcp-r">Round</th><th>Due (your time)</th><th>Time left</th>
                  <th>Event status</th><th>Your status</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.event_id} className="srcp-click" onClick={() => openEvent(e.event_id)}>
                    <td className="srcp-ell srcp-ell--wide" title={e.title}>
                      <Link className="srcp-strong srcp-rowlink" to={`/sourcing-portal/events/${encodeURIComponent(e.event_id)}`}
                        onClick={(ev) => ev.stopPropagation()}>{e.title}</Link>
                    </td>
                    <td className="srcp-r srcp-num">{e.round || ''}</td>
                    <td>{formatDueAt(e.due_at) || 'No due time set'}</td>
                    <td>{e.status === 'open' ? timeLeftText(e.due_at, now) : ''}</td>
                    <td><EventStatus status={e.status} /></td>
                    <td><span className={`srcp-pill${OWN_TONE[e.own_status] ? ` ${OWN_TONE[e.own_status]}` : ''}`}>
                      {OWN_STATUS_TEXT[e.own_status] || e.own_status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="srcp-card">
        <div className="srcp-card-h">
          <span className="srcp-card-t">Messages</span>
          <span className="srcp-mut">Notices from this buyer, unread first.</span>
        </div>
        {notices.length === 0 && <div className="srcp-card-b srcp-mut">No messages.</div>}
        {notices.length > 0 && (
          <div className="srcp-scroll">
            <table className="srcp-grid">
              <thead><tr><th>Date</th><th>Subject</th><th>Message</th><th>Status</th><th><span className="srcp-sr">Actions</span></th></tr></thead>
              <tbody>
                {notices.map((n) => (
                  <tr key={n.notice_id} className={n.read ? undefined : 'srcp-unread'}>
                    <td>{formatDueAt(n.created_at)}</td>
                    <td className={`srcp-ell${n.read ? '' : ' srcp-strong'}`} title={n.title}>{n.title}</td>
                    <td className="srcp-ell srcp-ell--wide srcp-notes" title={n.body || ''}>{n.body || ''}</td>
                    <td><span className={`srcp-pill${n.read ? '' : ' srcp-pill--info'}`}>{n.read ? 'Read' : 'Unread'}</span></td>
                    <td className="srcp-r">
                      <span className="srcp-row" style={{ justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                        {!n.read && (
                          <button type="button" className="srcp-btn srcp-btn--sm" onClick={() => markRead(n.notice_id)}>Mark read</button>
                        )}
                        {n.event_id && (
                          <Link className="srcp-btn srcp-btn--sm" to={`/sourcing-portal/events/${encodeURIComponent(n.event_id)}`}>Open the event</Link>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

function Home() {
  const { me } = usePortalSession();
  const buyers = me.buyers || [];
  const [chosen, setChosen] = useState(() => pickInitialBuyer(buyers));

  const choose = (membershipId) => {
    setChosenBuyer(membershipId);
    setChosen(membershipId);
  };

  if (buyers.length === 0) {
    return (
      <div className="srcp-empty">
        <div className="srcp-strong">No buyers yet</div>
        Open the invitation link a buyer sent you to join an event.
      </div>
    );
  }
  const buyer = buyers.find((b) => b.membership_id === chosen);
  return (
    <>
      <div className="srcp-titlerow">
        <div className="srcp-titlegrow"><h1 className="srcp-h1">{buyer ? buyer.buyer_name : 'Your events'}</h1></div>
        {zoneName() && <span className="srcp-mut">Times show in your time zone: {zoneName()}</span>}
      </div>
      {buyers.length > 1 && (
        <div className="srcp-chips" role="tablist" aria-label="Buyers">
          {buyers.map((b) => (
            <button key={b.membership_id} type="button" role="tab" aria-selected={b.membership_id === chosen}
              className={`srcp-chip${b.membership_id === chosen ? ' srcp-chip--on' : ''}`}
              onClick={() => choose(b.membership_id)}>{b.buyer_name}</button>
          ))}
        </div>
      )}
      {!buyer && <div className="srcp-empty">Choose a buyer to see their events.</div>}
      {buyer && <BuyerView key={buyer.membership_id} membershipId={buyer.membership_id} />}
    </>
  );
}

export default function HomePage() {
  return <PortalShell><Home /></PortalShell>;
}
